import React, { useState } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { uploadAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const FileUpload = ({ 
  type = 'image', // 'image', 'brochure', 'logo'
  currentFile = null,
  onUploadSuccess,
  accept,
  label = 'Upload File',
  maxSize = 10 // MB
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentFile);
  const { toast } = useToast();

  const getUploadFunction = () => {
    switch (type) {
      case 'image':
        return uploadAPI.uploadImage;
      case 'brochure':
        return uploadAPI.uploadBrochure;
      case 'logo':
        return uploadAPI.uploadLogo;
      default:
        return uploadAPI.uploadImage;
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      toast({
        title: 'Error',
        description: `File size must be less than ${maxSize}MB`,
        variant: 'destructive'
      });
      return;
    }

    setUploading(true);

    try {
      // Show preview for images
      if (type === 'image' || type === 'logo') {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(file);
      }

      // Upload file
      const uploadFunc = getUploadFunction();
      const result = await uploadFunc(file);
      
      // Construct full URL
      const fullUrl = process.env.REACT_APP_BACKEND_URL + result.url;
      
      setPreview(fullUrl);
      
      if (onUploadSuccess) {
        onUploadSuccess(fullUrl, result.filename);
      }

      toast({
        title: 'Success',
        description: 'File uploaded successfully'
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to upload file',
        variant: 'destructive'
      });
      setPreview(currentFile);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    if (onUploadSuccess) {
      onUploadSuccess(null, null);
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      
      {preview && (type === 'image' || type === 'logo') ? (
        <div className="relative inline-block">
          <img
            src={preview}
            alt="Preview"
            className="w-32 h-32 object-cover rounded-lg border"
          />
          <button
            type="button"
            onClick={handleRemove}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : preview && type === 'brochure' ? (
        <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">File uploaded</p>
            <p className="text-xs text-gray-500">{preview}</p>
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="text-red-500 hover:text-red-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="flex items-center space-x-2">
          <input
            type="file"
            onChange={handleFileChange}
            accept={accept}
            disabled={uploading}
            className="hidden"
            id={`file-upload-${type}`}
          />
          <label
            htmlFor={`file-upload-${type}`}
            className="cursor-pointer"
          >
            <Button
              type="button"
              variant="outline"
              disabled={uploading}
              asChild
            >
              <span>
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Choose File
                  </>
                )}
              </span>
            </Button>
          </label>
        </div>
      )}
      
      <p className="text-xs text-gray-500">
        {type === 'brochure' 
          ? `PDF, DOC, DOCX (Max ${maxSize}MB)`
          : `JPG, PNG, GIF, SVG (Max ${maxSize}MB)`
        }
      </p>
    </div>
  );
};

export default FileUpload;
